import DOMPurify from 'isomorphic-dompurify';
import * as htmlparser2 from 'htmlparser2';

interface HTMLSanitizerOptions {
  allowedTags?: Array<string>;
  allowedAttrs?: Array<string>; // Applies to any tag
  keepContent?: boolean;
  maxSyncLength?: number; // If content length <= maxSyncLength, runs sync
}

export class HTMLSanitizer {
  static sanitize(text: string, options: HTMLSanitizerOptions = {}): string | Promise<string> {
    if (!text) return text;

    const maxSync = options.maxSyncLength ?? 10000;
    if (text.length <= maxSync) return HTMLSanitizer.sanitizeSync(text, options);
    return HTMLSanitizer.sanitizeAsync(text, options);
  }

  static sanitizeSync(text: string, options: HTMLSanitizerOptions = {}): string {
    if (!text) return text;

    return DOMPurify.sanitize(text, {
      ALLOWED_TAGS: options.allowedTags || [],
      ALLOWED_ATTR: options.allowedAttrs || [],
      KEEP_CONTENT: options.keepContent ?? true,
    });
  }

  static sanitizeAsync(text: string, options: HTMLSanitizerOptions = {}): Promise<string> {
    if (!text) return Promise.resolve(text);

    const { allowedTags = [], allowedAttrs = [], keepContent = false } = options;
    const allowedTagsSet = new Set(allowedTags);
    const allowedAttrsSet = new Set(allowedAttrs);

    if (allowedTagsSet.size === 0 && allowedAttrsSet.size === 0 && !keepContent)
      return Promise.resolve(HTMLSanitizer.escapeHtml(text));

    return new Promise((resolve) => {
      let output = '';
      const openTags: string[] = [];
      const ESCAPED_BUT_KEEP_CONTENT_TAG = 'ESCAPED_BUT_KEEP_CONTENT';
      const ESCAPED_TAG = 'ESCAPED';

      const parser = new htmlparser2.Parser(
        {
          onopentag(name, attribs) {
            const IS_TAG_ALLOWED = allowedTagsSet.has(name);

            if (IS_TAG_ALLOWED) {
              openTags.push(name);
              const attrTuples = Object.entries(attribs);
              const attrs = attrTuples
                .filter(([attrName]) => allowedAttrsSet.has(attrName))
                .map(([attrName, attrValue]) => {
                  const IS_ATTR_ALLOWED = allowedAttrsSet.has(attrName);
                  return IS_ATTR_ALLOWED ? `${attrName}="${HTMLSanitizer.escapeHtml(attrValue)}"` : null;
                })
                .join(' ');
              output += `<${name}${attrs ? ' ' + attrs : ''}>`;
              return;
            }

            if (keepContent) return openTags.push(ESCAPED_BUT_KEEP_CONTENT_TAG);
            return openTags.push(ESCAPED_TAG);
          },

          ontext(text) {
            const LATEST_OPEN_TAG = openTags[openTags.length - 1];
            if (LATEST_OPEN_TAG === ESCAPED_TAG) return;
            output += HTMLSanitizer.escapeHtml(text);
          },

          onclosetag(tagname) {
            const latestTag = openTags.pop();
            if (latestTag === tagname) {
              output += `</${tagname}>`;
              return;
            }

            if (latestTag === ESCAPED_BUT_KEEP_CONTENT_TAG) {
              // Skipped tag but kept content
            }
          },
        },
        { decodeEntities: true },
      );

      parser.write(text);
      parser.end();
      resolve(output);
    });
  }

  private static escapeHtml(str: string) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
